import {
  type Attributes,
  type FocusEventHandler,
  type HTMLAttributes,
  type KeyboardEvent,
  type MouseEvent,
  type ReactElement,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import cx from 'classnames'
import {
  useCombobox,
  type UseComboboxReturnValue,
  type UseComboboxState,
  type UseComboboxStateChange,
} from 'downshift'
import { type Observable } from 'rxjs'
import { useObservableFocus, useFocusTracking } from '@/hooks/focus-hooks'
import { IconButton } from '@/components/IconButton'
import { Chip } from '@/components/Chip'
import { type IconName } from '@/components/Icon'
import { Spinner } from '@/components/Spinner'
import { SearchResultItem } from './SearchResultItem'
import styles from './CustomSelect.module.css'
import { SearchResults } from './SearchResults'
import { isNotNil } from '@/utils/object-util'
import { type ControlStatus } from '@/components/inputs/control-status'
import {
  type ControlSize,
  useControlSize,
} from '@/components/inputs/control-size'
import { match } from '@/utils/pattern-match-util'
import { useFloating, autoUpdate, shift, size } from '@floating-ui/react'

/**
 * Props for the CustomSelect component.
 */
export interface CustomSelectProps<
  V, // Value type
  M extends true | false, // Whether multi is on or not.
  VT = M extends false ? V : V[], // The value type, conditional based on multi
> extends Partial<CustomSelectState<V>> {
  /**
   * The current value. If `multi`, an array should be specified.
   */
  value: VT | undefined
  /**
   * Whether this is a multi select.
   */
  multi?: M
  /**
   * If `true`, the menu will stay open when a selection is made.
   */
  persist?: boolean
  /**
   * Whether the component is disabled.
   */
  isDisabled?: boolean
  /**
   * Control status.
   */
  status?: ControlStatus
  /**
   * Defaults to the size of the surrounding `ControlSizeContext`.
   */
  size?: ControlSize
  /**
   * Placeholder text to use for the search field when no selection exists.
   */
  placeholder?: string
  /**
   * Indicates whether search results should be show above or below the input.
   */
  searchResultsPlacement?: 'above' | 'below'
  /**
   * The search field value.
   */
  inputValue: string

  /**
   * Callback for triggering loading more.
   */
  onLoadMore(): unknown

  /**
   * Callback for changing the search field value.
   * @param value
   */
  onInputValueChange(value: string): unknown

  /**
   * If provided, triggers input focus when a value is emitted.
   */
  focus$?: Observable<unknown>

  /**
   * Focus event.
   */
  onFocus?: FocusEventHandler

  /**
   * Blur event.
   */
  onBlur?: FocusEventHandler

  /**
   * Change handler. If single select, `null` means clear. If multi, an empty array will
   * mean clear.
   * @param newValue
   */
  onChange: (newValue: M extends false ? VT | undefined : VT) => unknown

  /**
   * If `undefined` (default), renders an arrow icon.
   * If `null`, renders no icon.
   * If specified, renders the given icon.
   */
  icon?: null | IconName

  /**
   * Color of the toggle icon. Defaults to the surrounding text color.
   */
  iconColor?: string

  /**
   * Used when not providing a `renderItem` and/or `renderSearchItem` impl.
   */
  itemToString?: (item: V) => string

  /**
   * If specified, used for comparing whether items are equal.
   * Used for filtering out selected items from search result.
   */
  areItemsEqual?: (
    left: V | undefined | null,
    right: V | undefined | null,
  ) => boolean

  /**
   * If specified, used for rendering selected items when `multi` is `true`.
   */
  renderItem?(itemProps: RenderItemProps<V>): ReactElement

  /**
   * If specified, used for rendering the search items.
   */
  renderSearchItem?(itemProps: RenderSearchItemProps<V>): ReactElement

  /**
   * Called when `Enter` is downed while not trying to select an item.
   *
   * @param e
   */
  onEnterDown?(e: KeyboardEvent): unknown

  /**
   * Whether the status of the selector passes validation.
   */
  isNotValid?: boolean

  /**
   * Test selector.
   */
  testId?: string
}

/**
 * Props for rendering an item in a multi select.
 */
export interface RenderItemProps<V> {
  item: V
  isDisabled: boolean
  removeItem: () => unknown
  itemToString: (item: V) => string
  focusInputField: () => unknown
  toggleMenu: () => unknown
  testId?: string
}

/**
 * Props for rendering a search result item.
 */
export interface RenderSearchItemProps<V> extends HTMLAttributes<HTMLElement> {
  item: V
  selected: boolean
  highlighted: boolean
  itemToString: (v: V) => string
  testId?: string
}

/**
 * State shape.
 */
export interface CustomSelectState<V> {
  /**
   * The search results.
   */
  searchResults: readonly V[]
  /**
   * Whether we are loading.
   */
  isLoading: boolean
  /**
   * Whether we have reached the end of stream.
   */
  isEoS: boolean
}

/**
 * Custom Select State helper.
 */
export const CustomSelectState = {
  initial: {
    isLoading: false,
    isEoS: true,
    searchResults: [],
  } as CustomSelectState<never>,
}

/**
 * Custom select component.
 */
export function CustomSelect<V, M extends boolean>({
  value,
  isDisabled,
  status,
  size: sizeProp,
  multi,
  onChange,
  placeholder,
  icon,
  iconColor,
  persist,
  onEnterDown,
  onBlur,
  onFocus,
  focus$,
  isEoS,
  isLoading,
  isNotValid,
  testId,
  searchResults = [],
  inputValue,
  onInputValueChange,
  onLoadMore,
  itemToString: actualItemToString = String,
  areItemsEqual = defaultAreItemsEqual,
  renderItem = multi ? defaultRenderItem : defaultRenderSingleItem,
  renderSearchItem = defaultRenderSearchItem,
  searchResultsPlacement = 'below',
}: CustomSelectProps<V, M>) {
  const controlSize = useControlSize(sizeProp)
  const [inputEl, setInputEl] = useState<HTMLInputElement | null>(null)
  // `focus$` can emit in the same commit that mounts the input, before
  // `inputEl` is rendered, so the request waits for the element.
  const [focusRequestCount, setFocusRequestCount] = useState(0)

  const {
    x: left,
    y: top,
    refs: { setReference, setFloating, reference, floating },
    strategy,
    update,
  } = useFloating({
    strategy: 'fixed',
    middleware: [
      shift(),
      // Measured here rather than while rendering, which can happen while a
      // dialog is still scaling in and would keep the scaled size.
      size({
        apply({ rects, elements }) {
          Object.assign(elements.floating.style, {
            maxWidth: `${rects.reference.width}px`,
            maxHeight: `${rects.reference.height}px`,
          })
        },
      }),
    ],
  })

  const itemToString = useCallback(
    (item: V | undefined) => (isNotNil(item) ? actualItemToString(item) : ''),
    [actualItemToString],
  )

  // Whether we currently have anything selected.
  const hasSelection = multi
    ? (value as V[])?.length > 0
    : !(value === null || value === undefined)
  const focusInputField = useCallback(() => inputEl?.focus(), [inputEl])

  // TS can't narrow the conditional `onChange` parameter type from `multi`;
  // the public props stay type-safe.
  const emitChange = onChange as (
    newValue: V | V[] | null | undefined,
  ) => unknown

  useObservableFocus(focus$, () => setFocusRequestCount((n) => n + 1))
  useEffect(() => {
    if (focusRequestCount > 0) {
      inputEl?.focus()
    }
  }, [focusRequestCount, inputEl])

  // Tracks focus of the input field.
  const [focusProps, isFocused] = useFocusTracking({
    onBlur,
    onFocus,
  })

  // When using multi select, we don't want to display
  // items already selected.
  const filteredSearchResults = useMemo(() => {
    if (multi) {
      return searchResults.filter(
        (left) => !(value as V[]).some((right) => areItemsEqual(left, right)),
      )
    }
    return [...searchResults]
  }, [searchResults, value, multi, areItemsEqual])

  // Can be used to clear the selection.
  const removeItem = useCallback(
    (item: V) => {
      if (multi) {
        emitChange((value as V[]).filter((v) => v !== item))
      } else {
        emitChange(undefined)
      }
      focusInputField()
    },
    [emitChange, value, focusInputField, multi],
  )

  const change = useCallback(
    (e: Partial<UseComboboxState<V | null>>) => {
      const item = e.selectedItem
      if (multi) {
        if (item === null) {
          emitChange([])
        } else {
          if (!(value as V[]).some((v) => areItemsEqual(v, item))) {
            emitChange([...(value as V[]), item as V])
          }
        }
      } else {
        if (areItemsEqual(value as V | undefined, item)) {
          return
        }

        emitChange(item)
      }
    },
    [emitChange, value, multi, areItemsEqual],
  )

  // Focus the input when clicking the input box.
  const onInputBox = useCallback(
    (e: MouseEvent) => {
      if (e.currentTarget === e.target) {
        focusInputField()
      }
    },
    [focusInputField],
  )

  const onStateChange = useCallback(
    (e: UseComboboxStateChange<V>) => {
      if (e.selectedItem) {
        onInputValueChange('')
        return
      }

      switch (e.type) {
        case useCombobox.stateChangeTypes.FunctionCloseMenu:
        case useCombobox.stateChangeTypes.InputBlur:
          onInputValueChange('')
          break
      }
    },
    [onInputValueChange],
  )

  const onDownshiftInputValueChange = useCallback(
    (e: Partial<UseComboboxState<V>>) => {
      if (e.inputValue === null || e.inputValue === undefined) {
        return
      }

      // If single select, and we already have a selection, prevent further
      // input.
      if (!multi && hasSelection) {
        return
      }

      onInputValueChange(e.inputValue)
    },
    [onInputValueChange, multi, hasSelection],
  )

  const downshift: UseComboboxReturnValue<V> = useCombobox<V>({
    onStateChange,
    inputValue,
    items: filteredSearchResults,
    onInputValueChange: onDownshiftInputValueChange,
    selectedItem: null,
    onSelectedItemChange: change,
    itemToString: emptyStringGetter,
    // Ensures that the menu stays open when selecting items in a multi select
    stateReducer(_, actionAndChanges) {
      const { changes, type } = actionAndChanges
      switch (type) {
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
          return persist
            ? { ...changes, isOpen: true }
            : { ...changes, isOpen: false }

        default:
          return changes
      }
    },
  })

  // The results only render while open, so that's the only time their
  // position needs tracking.
  useEffect(() => {
    if (downshift.isOpen && reference.current && floating.current) {
      return autoUpdate(reference.current, floating.current, update, {
        animationFrame: true,
      })
    }
  }, [downshift.isOpen, update, floating, reference])

  const { closeMenu, highlightedIndex } = downshift

  // Various nice shortcuts.
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      // Deletes the last item if backspacing an empty input field.
      if (
        event.key === 'Backspace' &&
        !(event.target as HTMLInputElement).value
      ) {
        if (multi) {
          const arr = value as V[]
          if (arr.length > 0) {
            emitChange(arr.slice(0, arr.length - 1))
          }
        } else {
          emitChange(undefined)
        }
      }

      // Don't clear selection on Esc, clear input value, close menu.
      if (event.key === 'Escape') {
        ;(
          event.nativeEvent as Event & { preventDownshiftDefault?: boolean }
        ).preventDownshiftDefault = true
        onInputValueChange('')
        closeMenu()
      }

      if (event.key === 'Enter' && highlightedIndex === null) {
        onEnterDown?.(event)
      }
    },
    [
      value,
      multi,
      onEnterDown,
      emitChange,
      onInputValueChange,
      highlightedIndex,
      closeMenu,
    ],
  )

  return (
    // The whole control is the menu so clicks on the toggle button and the
    // selected items count as inside it and don't blur the combobox.
    <div
      className={styles.root}
      {...downshift.getMenuProps()}
      data-test={testId}
    >
      <div
        ref={setReference}
        className={cx(
          styles.control,
          match(controlSize, {
            normal: undefined,
            compact: styles.compact,
            small: styles.small,
          }),
          isDisabled && styles.disabled,
          status && styles[status],
          isFocused && styles.focused,
          isNotValid && styles.isNotValid,
        )}
      >
        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- mouse convenience around the input, which is the keyboard target */}
        <div
          className={cx(styles.inputBox, multi && styles.multiInputBox)}
          onClick={onInputBox}
        >
          {multi
            ? (value as V[]).map((item, i) => (
                // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- only keeps clicks on a selected item from refocusing the input
                <div
                  key={i}
                  className={styles.selectedItem}
                  onClick={(e) => e.stopPropagation()}
                >
                  {renderItem({
                    item,
                    testId: 'selected-item',
                    itemToString,
                    isDisabled: !!isDisabled,
                    focusInputField,
                    toggleMenu: downshift.toggleMenu,
                    removeItem: () =>
                      isDisabled ? undefined : removeItem(item),
                  })}
                </div>
              ))
            : value !== undefined &&
              value !== null && (
                // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- only keeps clicks on the selected item from refocusing the input
                <div
                  className={styles.selectedItem}
                  onClick={(e) => e.stopPropagation()}
                >
                  {renderItem({
                    item: value as V,
                    itemToString,
                    testId: 'selected-item',
                    isDisabled: !!isDisabled,
                    toggleMenu: downshift.toggleMenu,
                    focusInputField,
                    removeItem: () =>
                      isDisabled ? undefined : removeItem(value as V),
                  })}
                </div>
              )}
          <div className={styles.inputContainer}>
            <input
              type="text"
              className={styles.input}
              autoCapitalize="off"
              autoCorrect="off"
              data-test={testId && `${testId}-input`}
              disabled={isDisabled}
              {...downshift.getInputProps({
                onFocus: focusProps.onFocus,
                onBlur: focusProps.onBlur,
                onKeyDown: handleKeyDown,
                ref: setInputEl,
              })}
              placeholder={hasSelection ? '' : placeholder}
              style={{
                width: 2 + inputValue.length * 7,
              }}
            />
          </div>
        </div>
        <div className={styles.side}>
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- mouse convenience; the input is the keyboard target */}
          <div
            onClick={focusInputField}
            className={cx(
              styles.loading,
              isLoading && downshift.isOpen && styles.showLoading,
            )}
          >
            <Spinner size={16} thickness={2} />
          </div>

          {icon !== null && (
            <div className={styles.actions}>
              <IconButton
                name={icon ?? 'arrow'}
                label={downshift.isOpen ? 'Close options' : 'Open options'}
                testId={testId && `${testId}-toggle-dropdown-button`}
                inheritColor
                isDisabled={isDisabled}
                onPress={() => {
                  if (downshift.isOpen) {
                    downshift.closeMenu()
                  } else {
                    downshift.openMenu()
                  }
                }}
                rotate={icon ? undefined : 90}
                size={controlSize === 'normal' ? undefined : 16}
                color={iconColor}
              />
            </div>
          )}
        </div>
      </div>
      <div
        ref={setFloating}
        className={styles.resultsContainer}
        style={{
          position: strategy,
          top: top ?? 0,
          left: left ?? 0,
        }}
      >
        {downshift.isOpen && !isDisabled && (
          <SearchResults
            testId="search-result"
            placement={searchResultsPlacement}
            isLoading={isLoading || false}
            onLoadMore={onLoadMore}
            areItemsEqual={areItemsEqual}
            value={value}
            filteredSearchResults={filteredSearchResults}
            itemToString={itemToString}
            renderSearchItem={renderSearchItem}
            highlightedIndex={downshift.highlightedIndex}
            getItemProps={downshift.getItemProps}
            isEoS={isEoS || false}
            multi={Boolean(multi)}
          />
        )}
      </div>
    </div>
  )
}

/**
 * Default search result item renderer.
 */
export function defaultRenderSearchItem<V>({
  item,
  itemToString,
  ...props
}: RenderSearchItemProps<V>) {
  const itemAsString = itemToString(item)
  return <SearchResultItem {...props}>{itemAsString}</SearchResultItem>
}

/**
 * Default multi-select item renderer.
 */
export function defaultRenderItem<V>(props: Attributes & RenderItemProps<V>) {
  return <MultiItemSelectedDefault {...props} />
}

/**
 * Props for the `MultiItemSelectedDefault` component.
 */
export interface MultiItemSelectedDefaultProps<V> extends RenderItemProps<V> {
  /**
   * An icon or something to render next to the text.
   */
  visual?: ReactNode
}

/**
 * A selected item in a multi select, shown as a removable chip.
 */
export function MultiItemSelectedDefault<V>({
  item,
  isDisabled,
  itemToString,
  visual,
  removeItem,
  testId,
}: MultiItemSelectedDefaultProps<V>) {
  const itemAsString = itemToString(item)
  return (
    <div className={styles.defaultMultiSelectedItem}>
      <Chip onRemove={isDisabled ? undefined : removeItem} testId={testId}>
        {visual && (
          <span className={styles.defaultSingleSelectedItemVisual}>
            {visual}
          </span>
        )}
        <span className={styles.defaultSingleSelectedMutliItemText}>
          {itemAsString}
        </span>
      </Chip>
    </div>
  )
}

/**
 * Default single-select item renderer.
 * Designed to look like an input field.
 */
export function defaultRenderSingleItem<V>(
  props: Attributes & RenderItemProps<V>,
) {
  return <SingleItemSelectedDefault {...props} />
}

/**
 * Props for the `SingleItemSelectedDefault` component.
 */
export interface SingleItemSelectedDefaultProps<V> extends RenderItemProps<V> {
  /**
   * An icon or something to render next to the text.
   */
  visual?: ReactNode
}

/**
 * The selected item in a single select, styled like the input's value.
 */
export function SingleItemSelectedDefault<V>({
  item,
  isDisabled,
  itemToString,
  focusInputField,
  toggleMenu,
  visual,
  testId,
}: SingleItemSelectedDefaultProps<V>) {
  const onClick = useCallback(() => {
    toggleMenu()
    focusInputField()
  }, [focusInputField, toggleMenu])
  const itemAsString = itemToString(item)
  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events -- mouse shortcut for opening the menu; keyboard users open it from the focused input
    <div
      onClick={isDisabled ? undefined : onClick}
      className={styles.defaultSingleSelectedItem}
      data-test={testId}
    >
      {visual && (
        <span className={styles.defaultSingleSelectedItemVisual}>{visual}</span>
      )}
      <span className={styles.defaultSingleSelectedItemText}>
        {itemAsString}
      </span>
    </div>
  )
}

/**
 * Default implementation of `areItemsEqual`.
 */
function defaultAreItemsEqual(left: unknown, right: unknown) {
  return left === right
}

/**
 * Stable function for getting an empty string.
 */
function emptyStringGetter() {
  return ''
}
